import * as React from 'react'

import { createContext } from '~logic/lib/react'
import { getRadiusValue, TRadius } from '~ui/styleGuide/radiuses'
import { TSpace } from '~ui/styleGuide/spaces'

import { useModalPadding } from '../hooks/useModalPadding'

type TModalSettings = {
  radiusValue: string
  bodyPadding: string
  headerPadding: string
  footerPadding: string
  paddingHorizontalValue: string
}

const [Provider, useModalSettingsCtx] = createContext<TModalSettings>(`ModalSettings`)

type TProps = RequiredChildren & {
  radius: TRadius
  padding: TSpace
  compactPadding: boolean
}

/**
 * provide some common modal settings
 * so it can be set once on the Modal component
 * and used by modal parts
 * ! should be used only by the base Modal implementation
 */
export const ModalSettingsProvider: React.FC<TProps> = ({
  padding,
  radius,
  children,
  compactPadding,
}) => {
  const radiusValue = getRadiusValue(radius)

  // * sync padding values across all of the Modal’s sub-components (unless it’s overridden)

  const {
    paddingValue, //
    paddingVerticalValue,
    paddingHorizontalValue,
    paddingVerticalHalfValue,
  } = useModalPadding({ padding })

  const headerPadding = React.useMemo(() => {
    // if the padding should be compact, cut the bottom padding of the head
    if (compactPadding) {
      return `${paddingVerticalValue} ${paddingHorizontalValue} ${paddingVerticalHalfValue} ${paddingHorizontalValue}`
    }

    return paddingValue
  }, [
    paddingValue,
    compactPadding,
    paddingVerticalValue,
    paddingHorizontalValue,
    paddingVerticalHalfValue,
  ])

  const bodyPadding = React.useMemo(() => {
    // if the padding should be compact, cut the vertical padding of the body
    if (compactPadding) {
      return `${paddingVerticalHalfValue} ${paddingHorizontalValue} ${paddingVerticalHalfValue} ${paddingHorizontalValue}`
    }

    return paddingValue
  }, [paddingValue, compactPadding, paddingHorizontalValue, paddingVerticalHalfValue])

  const footerPadding = React.useMemo(() => {
    // if the padding should be compact, cut the top padding of the footer
    if (compactPadding) {
      return `${paddingVerticalHalfValue} ${paddingHorizontalValue} ${paddingVerticalValue} ${paddingHorizontalValue}`
    }

    return paddingValue
  }, [
    paddingValue,
    compactPadding,
    paddingVerticalValue,
    paddingHorizontalValue,
    paddingVerticalHalfValue,
  ])

  return (
    <Provider
      value={{ headerPadding, bodyPadding, footerPadding, paddingHorizontalValue, radiusValue }}
    >
      {children}
    </Provider>
  )
}

export { useModalSettingsCtx }
